import { memo, useCallback } from "react";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import classNames from "classnames";
import { ServiceTemplateRecurrenceOccurrenceStatus } from "@common/types/serviceTemplateRecurrenceOccurrence";
import { Button } from "@design-system/Button/Button";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import viewTableStyles from "@design-system/ViewTable/ViewTable.module.scss";
import { useViewTableMeta } from "@hooks/view/useViewTableMeta";
import type {
    ServiceTemplateScheduleOccurrenceCallbacks,
    ServiceTemplateScheduleOccurrenceTableOptions,
} from "../serviceTemplateScheduleOccurrenceCallbacks";
import { ServiceTemplateScheduleOccurrenceRow } from "../serviceTemplateScheduleOccurrencesViewConfig";
import { getOccurrenceDisplayStatus } from "../serviceTemplateScheduleOccurrenceStatus.util";
import styles from "./ServiceTemplateScheduleOccurrenceStatusCell.module.scss";

export const ServiceTemplateScheduleOccurrenceStatusCell = memo(
    ({
        getValue,
        row,
        table,
    }: CellContext<
        ViewTableFeatures,
        ServiceTemplateScheduleOccurrenceRow,
        ServiceTemplateRecurrenceOccurrenceStatus | undefined
    >) => {
        const status = getValue();
        const { callbacks, options } = useViewTableMeta<
            ServiceTemplateScheduleOccurrenceCallbacks,
            ServiceTemplateScheduleOccurrenceTableOptions
        >(table);
        const { closeId, workspaceId, applicationDate } = row.original;

        const handleOpen = useCallback(() => {
            if (closeId && workspaceId) {
                callbacks?.onOpenWorkRow(workspaceId, closeId);
            }
        }, [callbacks, closeId, workspaceId]);

        if (!status) {
            return <ViewTableCell />;
        }

        const isApplied =
            status === ServiceTemplateRecurrenceOccurrenceStatus.APPLIED;

        const { label, tooltip } = getOccurrenceDisplayStatus({
            status,
            applicationDate,
            isTemplateArchived: options?.isTemplateArchived ?? false,
            isRecurrencePaused: options?.isRecurrencePaused ?? false,
        });

        const statusLabel = tooltip ? (
            <Tooltip content={tooltip}>
                <span
                    className={classNames(
                        styles.statusText,
                        isApplied && viewTableStyles.hideOnHover,
                    )}
                >
                    {label}
                </span>
            </Tooltip>
        ) : (
            <span
                className={classNames(
                    styles.statusText,
                    isApplied && viewTableStyles.hideOnHover,
                )}
            >
                {label}
            </span>
        );

        return (
            <ViewTableCell captureClicks={isApplied}>
                <div className={styles.statusContainer}>
                    {statusLabel}
                    {isApplied &&
                        (closeId && workspaceId ? (
                            <Button
                                variant="primary"
                                size="sm"
                                className={classNames(
                                    styles.openAction,
                                    viewTableStyles.showOnHover,
                                )}
                                onClick={handleOpen}
                            >
                                Open
                            </Button>
                        ) : (
                            <Button
                                variant="primary"
                                size="sm"
                                disabled
                                className={classNames(
                                    styles.openAction,
                                    viewTableStyles.showOnHover,
                                )}
                            >
                                Deleted
                            </Button>
                        ))}
                </div>
            </ViewTableCell>
        );
    },
);

ServiceTemplateScheduleOccurrenceStatusCell.displayName =
    "ServiceTemplateScheduleOccurrenceStatusCell";
